import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import {
  AimOutlined,
  AppstoreOutlined,
  ControlOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { ASSET_CLASSES, HISTORICAL_SCENARIO, selectors, Session } from 'state';
import {
  benchmarkComponentLines,
  summarizeGroup,
} from '../../SessionSettingsShared/helpers';
import { InstrumentSelectionContext } from '../../SessionSettings/instrumentSelectionContext';
import { useSelectedInstrumentCount } from '../../../../features/assetCatalogue/SessionCatalogue';

export type DenseView = 'general' | 'instruments' | 'benchmark' | 'macro';

interface DenseRailProps {
  active: DenseView;
  onSelect: (view: DenseView) => void;
  session: Session;
}

/** Three-option navigation rail: General Settings · Instrument Universe ·
 *  Benchmark & Alpha Monitoring. The instrument and benchmark options carry a
 *  bar-prefixed sublist summarising their contents. */
const DenseRail: React.FC<DenseRailProps> = ({ active, onSelect, session }) => {
  const bench = summarizeGroup('benchmark', session);
  const benchComponents = benchmarkComponentLines(session);
  const sector = summarizeGroup('sector', session);
  // Live selection (updates as rows are checked, before Save) when the shell
  // provides it; falls back to the saved session otherwise. Drives both the
  // count and the asset-class chips below.
  const instrumentSelection = useContext(InstrumentSelectionContext);
  const selectedIds =
    instrumentSelection?.selected ?? session.underlying_assets ?? [];
  const count = useSelectedInstrumentCount(selectedIds);
  const historical = session.scenario_type === HISTORICAL_SCENARIO;

  // Predetermined-intervals: frequency is vestigial (the authored date schedule drives the
  // step), so the rail shows the pacing against "scheduled dates" instead of a day count.
  const pacingValue = historical
    ? session.time_advancement_type === 'predetermined_intervals'
      ? `${session.historical_sleep_time}s. / scheduled dates`
      : `${session.historical_sleep_time}s. / ${session.frequency} ${
          session.frequency === 1 ? 'day' : 'days'
        }`
    : '5min. ticks';
  // Leverage is "ON" if any margin differs from 100% (1.0 = fully funded).
  const leverageOn = [
    session.fx_leverage,
    session.bond_leverage,
    session.equity_leverage,
    session.etf_equities_leverage,
    session.etf_commodities_leverage,
    session.futures_leverage,
  ].some(m => Math.round((m ?? 1) * 100) !== 100);

  // Playable asset classes derived from the (live) selected instruments. Uses
  // the id-list selector so the chips track the unsaved selection, matching the
  // count above.
  const playable = useSelector(
    selectors.playableAssetClassesForIds(selectedIds),
  );
  // Class 17 and class 19 intentionally share a label, so render one stable chip when both are selected.
  const playableNames = Array.from(
    new Set(
      (playable ?? []).map((ac: number) => ASSET_CLASSES[ac]).filter(Boolean),
    ),
  );

  const rowClass = (view: DenseView, extra?: string) =>
    [
      'set-studio__rail-row',
      extra ?? '',
      active === view ? 'set-studio__rail-row--active' : '',
    ]
      .filter(Boolean)
      .join(' ');

  return (
    <div className="set-studio__rail">
      <div className="set-studio__rail-head">
        <span className="set-studio__rail-title">Settings</span>
      </div>
      <div className="set-studio__rail-list">
        <button
          type="button"
          className={rowClass('general', 'set3b-rail-rich')}
          onClick={() => onSelect('general')}
        >
          <span className="set-studio__rail-icon">
            <ControlOutlined />
          </span>
          <span className="set-studio__rail-text">
            <span className="set-studio__rail-name">General Settings</span>
            <span className="set3b-rail-sublist">
              <span className="set3b-rail-subitem set3b-rail-kv">
                <b>Pacing</b>
                <i>{pacingValue}</i>
              </span>
              <span className="set3b-rail-subitem set3b-rail-kv">
                <b>Leverage</b>
                <i>{leverageOn ? 'ON' : 'OFF'}</i>
              </span>
              <span className="set3b-rail-subitem set3b-rail-aclist">
                <i>Metrics</i>
                <i>Allocations</i>
                <i>Other</i>
              </span>
            </span>
          </span>
        </button>

        <button
          type="button"
          className={rowClass('instruments', 'set3b-rail-rich')}
          onClick={() => onSelect('instruments')}
        >
          <span className="set-studio__rail-icon">
            <AppstoreOutlined />
          </span>
          <span className="set-studio__rail-text">
            <span className="set-studio__rail-name">Instrument Universe</span>
            <span className="set3b-rail-sublist">
              <span className="set3b-rail-subitem">
                <b>{count} Instruments</b>
              </span>
              {playableNames.length > 0 ? (
                <span className="set3b-rail-subitem set3b-rail-aclist">
                  {playableNames.map((name: string) => (
                    <i key={name}>{name}</i>
                  ))}
                </span>
              ) : null}
            </span>
          </span>
        </button>

        <button
          type="button"
          className={rowClass('benchmark', 'set3b-rail-rich')}
          onClick={() => onSelect('benchmark')}
        >
          <span className="set-studio__rail-icon">
            <AimOutlined />
          </span>
          <span className="set-studio__rail-text">
            <span className="set-studio__rail-name">Benchmark</span>
            <span className="set3b-rail-sublist">
              {benchComponents.length > 0 ? (
                <span className="set3b-rail-subitem set3b-rail-complist">
                  {benchComponents.map(c => (
                    <i key={c.label} className="set3b-rail-compline">
                      <span className="set3b-rail-comppct">{c.pct}</span>
                      <span className="set3b-rail-complabel">{c.label}</span>
                    </i>
                  ))}
                </span>
              ) : (
                <span className="set3b-rail-subitem">
                  <b>Benchmark</b>
                  <i>{bench.value}</i>
                </span>
              )}
              {historical ? (
                <span className="set3b-rail-subitem">
                  <b>Alpha monitoring</b>
                  <i>
                    {sector.value}
                    {sector.sub ? ` · ${sector.sub}` : ''}
                  </i>
                </span>
              ) : null}
            </span>
          </span>
        </button>

        {historical ? (
          <button
            type="button"
            className={rowClass('macro', 'set3b-rail-rich')}
            onClick={() => onSelect('macro')}
          >
            <span className="set-studio__rail-icon">
              <FileTextOutlined />
            </span>
            <span className="set-studio__rail-text">
              <span className="set-studio__rail-name">Macro Background</span>
              <span className="set3b-rail-sublist">
                <span className="set3b-rail-subitem">
                  <i>
                    {session.macro_background
                      ? 'Briefing set'
                      : 'No briefing yet'}
                  </i>
                </span>
              </span>
            </span>
          </button>
        ) : null}
      </div>
      <div className="set-studio__rail-foot">Dense layout</div>
    </div>
  );
};

export default DenseRail;
